<script setup>
import {onMounted, ref} from 'vue';
import router from '../../router.js';
import {activate, generate2faCode} from '@/services/AuthService';
import {getTenantData} from '@/services/TenantService';
import {useRoute} from 'vue-router';
import Footer from '@/components/layouts/Footer.vue';
import {maskPhoneNumber, setButtonContentDefault, setButtonContentLoading} from '@/helper';
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const userData = ref({});
const password = ref('');
const passwordConfirm = ref('');
const twoFactorCode = ref('');
const errorMessage = ref('');
const codeRequestInterval = ref(0);
const step = ref(1);
const theme = ref({});
const query = useRoute().query;
const codeSent = ref(false);
const codeSentAgain = ref(false);

onMounted(() => {
  getTenantData()
      .then((response) => {
        theme.value = response.theme;
      });

  userData.value = {
    email: query.email,
    phone: query.phone,
    user_id: query.user_id,
  }
});

const activateUser = () => {
  errorMessage.value = null;
  const form = {
    email: userData.value.email,
    password: password.value,
    password_confirmation: passwordConfirm.value,
    '2fa_code': twoFactorCode.value,
  }
  setButtonContentLoading('btn-activate');

  activate(form).then(response => {
    setButtonContentDefault('btn-activate');
    router.push('/home?welcome=true');
  },(error) => {
    if (error.message === 'user.already_active') {
      errorMessage.value = t('activation.user_already_active');
    }
    else if (error.message === 'verification_code.TOO_MANY_ATTEMPTS') {
      // vyčerpán počet pokusů o zadání kódu - BE kód dočasně vůbec neověřuje
      errorMessage.value = t('verification_code.TOO_MANY_ATTEMPTS');
    }
    else {
      errorMessage.value = t('activation.invalid_auth_code');
    }
    setButtonContentDefault('btn-activate');
  });
}

const generateCode = () => {
  codeSentAgain.value = true;
  errorMessage.value = null;
  const form = {
    verifiable_type: 'USER',
    verifiable_id: userData.value.user_id,
    service: 'SMS',
    recipient: userData.value.phone,
  }
  setButtonContentLoading('btn-activate');

  generate2faCode(form).then(response => {
    codeRequestInterval.value = 30;
    codeSent.value = true;
    setButtonContentDefault('btn-activate');
    const interval = setInterval(() => {
      codeRequestInterval.value--;
      if (codeRequestInterval.value <= 0) {
        clearInterval(interval);
        codeSentAgain.value = false;
      }
    }, 1000);
  },(error) => {
    errorMessage.value = error.message;
    setButtonContentDefault('btn-activate');
    codeSentAgain.value = false;
  });
}

const submit = () => {
  errorMessage.value = null;
  if (step.value === 1) {
    if (password.value !== passwordConfirm.value) {
      errorMessage.value = t('activation.passwords_not_match');
      return;
    }
    if (password.value.length < 8) {
      errorMessage.value = t('activation.passwords_must_be_8_char');
      return;
    }
    step.value = 2;
  } else {
    activateUser();
  }
}

</script>

<template>
  <div class="entrance">
    <div class="min-vh-100 row justify-content-center align-items-center">
      <div class="col-12 col-md-8 col-lg-6 col-xl-4">
        <div class="entrance__logo" v-if="theme && theme.logoBase64">
          <img :src="theme.logoBase64" v-if="theme && theme.logoBase64" alt="" />
        </div>
        <div class="entrance__title">
          {{ t('activation.activate_account') }}
        </div>
        <div class="entrance__content">
          <div class="card">
            <div class="card-body">
              <form>
                <div v-if="step === 1" class="form-group">
                  <div class="text-center mb-3">{{ t('activation.for_activation_set_password') }}</div>
                  <label class="form-label" for="password">{{ t('activation.password') }}</label>
                  <input id="password" class="form-control"
                         type="password" placeholder="" autocomplete="off"
                         v-model="password" required/>
                </div>
                <div v-if="step === 1" class="form-group">
                  <label class="form-label" for="password_confirm">{{ t('activation.confirm_password') }}</label>
                  <input id="password_confirm" class="form-control"
                          type="password" placeholder="" autocomplete="off"
                          v-model="passwordConfirm" required/>
                </div>
                <div v-if="step === 2" class="form-group">
                  <div class="text-center mb-3">{{ t('activation.for_activation_set_phone') }}</div>
                  <label class="form-label" for="2fa_code">{{ t('activation.phone_number2') }}</label>
                  <input id="2fa_code" class="form-control"
                         type="text" placeholder="" autocomplete="off" readonly disabled
                         :value="maskPhoneNumber(userData.phone)" required/>
                </div>
                <div v-if="step === 2 && codeSent" class="form-group">
                  <div class="form-label d-flex justify-content-between">
                    <label for="2fa_code">{{ t('activation.auth_code') }}</label>
                  </div>
                  <input id="2fa_code" class="form-control"
                         type="text" placeholder="" autocomplete="off"
                         v-model="twoFactorCode" required/>
                  <div class="mt-2">
                    <a :class="codeSentAgain === true ? 'pointer-events-none' : ''" class="cursor-pointer" @click="generateCode" v-if="codeRequestInterval === 0"><span>{{ t('activation.send_code') }} </span> <span v-if="codeSent"> {{ t('activation.again') }} </span> </a>
                    <span class="text-muted" v-if="codeRequestInterval > 0">{{ t('activation.auth_code_sent') }} {{codeRequestInterval}} {{ t('activation.secs') }}</span>
                  </div>
                </div>

                <div class="form-group">
                    <!-- Submit Button -->
                  <button v-if="step === 1 || (step === 2 && codeSent)" id="btn-activate" type="button" @click="submit" class="btn btn-primary w-100">
                    <span>{{step === 1 ? t('activation.set_password') : t('activation.verify_code') }}</span>
                  </button>
                  <button v-if="step === 2 && !codeSent" id="btn-activate" type="button" @click="generateCode" class="btn btn-primary w-100">
                    <span>{{ t('activation.send_auth_code') }}</span>
                  </button>
                  <div class="alert alert-danger mt-3" v-if="errorMessage"> {{errorMessage}} </div>
                </div>
                <div class="form-group form-group--footer-text">
                  <router-link to="/login?biometric=false">{{ t('activation.back_to_login') }}</router-link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer/>
  </div>
</template>
